import * as React from 'react';
import * as wijmo from '@grapecity/wijmo';

import * as wjChart from '@grapecity/wijmo.react.chart';
import * as wjGrid from '@grapecity/wijmo.grid';
import { FlexGrid, FlexGridColumn } from '@grapecity/wijmo.react.grid';

import { Col, Row, Container } from 'react-bootstrap';
import { useEffect, useState, useRef } from 'react';

import { toInt } from 'utils/numberUtils';
import './Analysis.css';

// 分析（相談件数）（工事／管・小）
function AnalysisSoudanBigSmall(props) {
  const gridRef = useRef(null);

  // 見積依頼リスト
  const quoteList = props.quoteList;
  // 移動年計（合計／リピート／新規の直近12か月合計、最大24か月分）
  const movingAnnualTotal = props.movingAnnualTotal ?? [];
  // 移動年計グラフ用：奇数月（7,9,11,1,3,5）の点だけに絞る
  const matChartData = movingAnnualTotal.filter((d) => parseInt(d.MONTH, 10) % 2 === 1);

  // 移動年計グラフ：期の境目に青の実線を引き、前期／今期の期名を描く
  function drawPeriodBoundary(chart, e) {
    const data = chart.itemsSource;
    if (!data || !data.length) return;

    const ax = chart.axisX;
    const ay = chart.axisY;
    const engine = e.engine;

    // 描画領域の上端・下端はY軸の変換から取得
    const yTop = ay.convert(ay.actualMax);
    const yBottom = ay.convert(ay.actualMin);
    if ([yTop, yBottom].some((v) => v == null || isNaN(v))) return;

    engine.fill = '#1565c0';
    // 7月の点すべてに、その真上へ青の実線を引き、ラベルの下に期名を描く
    data.forEach((d, i) => {
      if (d.MONTH !== '7月') return;
      const x = ax.convert(i);
      if (x == null || isNaN(x)) return;
      engine.drawLine(x, yTop, x, yBottom, null, {
        stroke: '#1565c0',
        'stroke-width': 2,
      });
      // X軸の月ラベルのさらに下に期名を表示
      engine.drawString(`${d.PERIOD}期`, new wijmo.Point(x - 12, yBottom + 40));
    });
  }

  // 工事のリピート件数
  const [bigRepeatCount, setBigRepeatCount] = useState(0);
  // 工事の新規件数
  const [bigNewCount, setBigNewCount] = useState(0);
  // 小工事のリピート件数
  const [smallRepeatCount, setSmallRepeatCount] = useState(0);
  // 小工事の新規件数
  const [smallNewCount, setSmallNewCount] = useState(0);

  useEffect(() => {
    if (gridRef.current) {
      updateFooter(gridRef.current.control);
    }

    // 工事のリピート・新規件数を計算
    setBigRepeatCount(
      quoteList.sourceCollection.reduce((sum, item) => sum + toInt(item.BIG_REPEAT, 0), 0)
    );
    setBigNewCount(
      quoteList.sourceCollection.reduce((sum, item) => sum + toInt(item.BIG_NEW, 0), 0)
    );

    // 小工事のリピート・新規件数を計算
    setSmallRepeatCount(
      quoteList.sourceCollection.reduce((sum, item) => sum + toInt(item.SMALL_REPEAT), 0)
    );
    setSmallNewCount(
      quoteList.sourceCollection.reduce((sum, item) => sum + toInt(item.SMALL_NEW, 0), 0)
    );
  }, [quoteList]);

  //  グリッドの初期化
  function onInitialized(grid) {
    // ヘッダーを2段にする
    var extraRow = new wjGrid.Row();
    extraRow.allowMerging = true;

    var panel = grid.columnHeaders;
    panel.rows.splice(0, 0, extraRow);

    for (let colIndex = 1; colIndex <= 2; colIndex++) {
      panel.setCellData(0, colIndex, 'ご相談件数');
    }
    for (let colIndex = 3; colIndex <= 4; colIndex++) {
      panel.setCellData(0, colIndex, 'リピート');
    }
    for (let colIndex = 5; colIndex <= 6; colIndex++) {
      panel.setCellData(0, colIndex, '新規');
    }

    // 垂直方向にマージ
    ['MONTH'].forEach(function (binding) {
      let col = grid.getColumn(binding);
      col.allowMerging = true;
      panel.setCellData(0, col.index, col.header);
    });
    // ヘッダーの中央揃え
    grid.formatItem.addHandler((sender, e) => {
      // ヘッダーセルのスタイル設定
      if (e.panel === sender.columnHeaders) {
        e.cell.style.textAlign = 'center';
        e.cell.style.display = 'flex';
        e.cell.style.justifyContent = 'center';
        e.cell.style.alignItems = 'center';
      }

      // フッターセルのスタイル設定
      if (e.panel === sender.columnFooters) {
        const colIndex = e.col;
        const rowIndex = e.row;

        // フッターの1行目の線を太くする
        if (rowIndex === 0) {
          e.cell.style.borderTop = 'double';
        }

        if (rowIndex == 0 && [1, 3, 5].includes(colIndex)) {
          // 前期
          e.cell.style.backgroundColor = '#d9e1f2';
        } else if (rowIndex == 0 && [2, 4, 6].includes(colIndex)) {
          // 今期
          e.cell.style.backgroundColor = '#ffff99';
        } else {
          // 他のセルの背景色をデフォルトに戻す
          e.cell.style.backgroundColor = '';
        }
      }
    });

    // フッターに１段追加
    grid.columnFooters.rows.push(new wjGrid.Row());

    // データ変更時に合計更新
    grid.collectionView.collectionChanged.addHandler(updateFooter);

    updateFooter(grid);
  }

  // 合計値を計算してフッターに設定
  function updateFooter(grid) {
    let data = grid?.itemsSource?.items;
    if (!data || data.length === 0) return;

    let panel = grid.columnFooters;

    grid.columns.forEach((col, colIndex) => {
      const binding = col.binding;
      if (binding === 'MONTH') {
        // 月
        panel.setCellData(0, colIndex, '合計');
      } else {
        // 小工事
        let total = data.reduce((sum, item) => sum + (item[binding] || 0), 0);
        panel.setCellData(0, colIndex, total);
      }
    });
  }

  return (
    <Container fluid className="p-0">
      <Row className="mt-2">
        <Col className="pl-3" md="5">
          <FlexGrid
            ref={gridRef}
            isReadOnly={true}
            allowMerging="ColumnHeaders"
            allowSorting={false}
            allowDragging={false}
            itemsSource={quoteList}
            headersVisibility={'Column'}
            alternatingRowStep={0}
            initialized={onInitialized}
          >
            <FlexGridColumn
              binding="MONTH"
              header="月"
              allowMerging={true}
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="BIG_TOTAL"
              cssClass="big_cell"
              header="工事"
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="SMALL_TOTAL"
              cssClass="small_cell"
              header="小・管"
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="BIG_REPEAT"
              cssClass="big_cell"
              header="工事"
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="SMALL_REPEAT"
              cssClass="small_cell"
              header={'小・管'}
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="BIG_NEW"
              cssClass="big_cell"
              header="工事"
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
            <FlexGridColumn
              binding="SMALL_NEW"
              cssClass="small_cell"
              header="小・管"
              format="n0"
              width={'*'}
              align="center"
            ></FlexGridColumn>
          </FlexGrid>
        </Col>
        <Col className="pl-3" md="4">
          <wjChart.FlexChart
            header="移動年計　ご相談件数"
            itemsSource={matChartData}
            bindingX="MONTH"
            chartType="LineSymbols"
            rendered={drawPeriodBoundary}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
          >
            {/* Y軸の最大・最小値を設定 */}
            <wjChart.FlexChartAxis wjProperty="axisY" min={0} />
            {/* X軸にY方向のグリッド線を表示 */}
            <wjChart.FlexChartAxis wjProperty="axisX" majorGrid={true} majorUnit={1} />
            {/* 凡例の位置を下に配置 */}
            <wjChart.FlexChartLegend position="Bottom" />
            <wjChart.FlexChartSeries name={`リピート 工事`} binding="REPEAT_BIG" symbolMarker="Dot" symbolSize={6} />
            <wjChart.FlexChartSeries name={`リピート 管・小`} binding="REPEAT_SMALL" symbolMarker="Dot" symbolSize={6} />
            <wjChart.FlexChartSeries name={`新規 工事`} binding="NEW_BIG" symbolMarker="Dot" symbolSize={6} />
            <wjChart.FlexChartSeries name={`新規 管・小`} binding="NEW_SMALL" symbolMarker="Dot" symbolSize={6} />
          </wjChart.FlexChart>
        </Col>
      </Row>
      <Row className="mt-2">
        <Col className="pl-3" md="6">
          <wjChart.FlexChart
            header={`工事　リピート・新規（相談件数）【累計】`}
            itemsSource={quoteList.sourceCollection}
            bindingX="MONTH"
            chartType="Line"
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
          >
            {/* Y軸の最大・最小値を設定 */}
            <wjChart.FlexChartAxis wjProperty="axisY" min={0} />
            {/* X軸にY方向のグリッド線を表示 */}
            <wjChart.FlexChartAxis wjProperty="axisX" majorGrid={true} />
            {/* 凡例の位置を下に配置 */}
            <wjChart.FlexChartLegend position="Bottom" />
            <wjChart.FlexChartSeries name={`リピート`} binding="BIG_REPEAT" />
            <wjChart.FlexChartSeries name={`新規`} binding="BIG_NEW" />
          </wjChart.FlexChart>
        </Col>
        <Col className="pl-3" md="6">
          <wjChart.FlexChart
            header={`管・小　リピート・新規（相談件数）【累計】`}
            itemsSource={quoteList.sourceCollection}
            bindingX="MONTH"
            chartType="Line"
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
          >
            {/* Y軸の最大・最小値を設定 */}
            <wjChart.FlexChartAxis wjProperty="axisY" min={0} />
            {/* X軸にY方向のグリッド線を表示 */}
            <wjChart.FlexChartAxis wjProperty="axisX" majorGrid={true} />
            {/* 凡例の位置を下に配置 */}
            <wjChart.FlexChartLegend position="Bottom" />
            <wjChart.FlexChartSeries name={`リピート`} binding="SMALL_REPEAT" />
            <wjChart.FlexChartSeries name={`新規`} binding="SMALL_NEW" />
          </wjChart.FlexChart>
        </Col>
      </Row>
      <Row className="mt-2">
        {/* 工事のリピート・新規件数の円グラフ */}
        <Col className="pl-3" md="6">
          <wjChart.FlexPie
            header={`工事　リピート・新規（相談件数）【累計】`}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            itemsSource={[
              {
                category: 'リピート',
                value: bigRepeatCount,
                rate: bigRepeatCount / (bigRepeatCount + bigNewCount),
              },
              {
                category: '新規',
                value: bigNewCount,
                rate: bigNewCount / (bigRepeatCount + bigNewCount),
              },
            ]}
            bindingName="category"
            binding="value"
            startAngle={90}
          >
            <wjChart.FlexPieDataLabel content="{name}({rate:p0})" position="Center" />
          </wjChart.FlexPie>
        </Col>
        {/* 小工事のリピート・新規件数の円グラフ */}
        <Col className="pl-3" md="6">
          <wjChart.FlexPie
            header={`管・小　リピート・新規（相談件数）`}
            style={{ backgroundColor: '#f7f7f8', borderRadius: '8px' }}
            itemsSource={[
              {
                category: 'リピート',
                value: smallRepeatCount,
                rate: smallRepeatCount / (smallRepeatCount + smallNewCount),
              },
              {
                category: '新規',
                value: smallNewCount,
                rate: smallNewCount / (smallRepeatCount + smallNewCount),
              },
            ]}
            bindingName="category"
            binding="value"
            startAngle={90}
          >
            <wjChart.FlexPieDataLabel content="{name}({rate:p0})" position="Center" />
          </wjChart.FlexPie>
        </Col>
      </Row>
    </Container>
  );
}
export default AnalysisSoudanBigSmall;
